/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DataLicensingRedistributionClauses.module.scss';

type Translate = (key: string) => string;

const getClauses = (t: Translate) => [
  { num: '01', quote: t('“You can use it internally, but you can’t pass it to your customers.”') },
  { num: '02', quote: t('“That licence covers Europe. Asia would be a separate deal.”') },
  { num: '03', quote: t('“You can’t use it to train or improve a model.”') },
  { num: '04', quote: t('“When the contract ends, you delete everything.”') },
  { num: '05', quote: t('“We can’t tell you which registry that came from.”') },
  { num: '06', quote: t('“There are no timestamps. We refresh the whole file quarterly.”') },
];

export const DataLicensingRedistributionClauses = () => {
  const { t } = useInt();
  const clauses = getClauses(t);
  const headRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const revealables = [headRef.current, ...rowRefs.current].filter((el): el is HTMLDivElement => Boolean(el));

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div ref={headRef} className={cn(styles.head, styles.reveal)}>
          <TitleBlock
            align="left"
            title={t('How many of these have you heard from a data vendor?')}
            description={t(
              'Coverage is the easy part. These six clauses sit in the agreement underneath it, and between them they decide what you can build, who you can sell it to, and what happens on the day you change supplier. A data licence is a product decision.',
            )}
          />
        </div>

        <div>
          {clauses.map((clause, i) => (
            <div
              key={clause.num}
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              className={cn(styles.row, styles.reveal)}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <span className={styles.num}>{clause.num}</span>
              <span className={styles.quote}>{clause.quote}</span>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
